import uniqBy from 'lodash/uniqBy';

import {
  BuilderMode,
  MealBuilderItemSection,
  SingleEntreeTypeBuilderMode,
  SelectableVariantMenuItem,
  SelectableMenuItem,
  ItemCardCustomization,
  ItemCardCustomizationType,
  SelectableItemCardCustomization,
  PortionItemType,
  SelectableMenuItemSource,
  PriceType,
} from '@/models';

import { AbstractMealBuilderItemVariantService } from '@/services/meal-builder/meal-builder-variant.service';
import { numberToWords, isDefined, isPortionItemType, sortFn, makeUniqueValue, getItemIds, getVariantsItemSectionTitle } from '@/helpers';
import {
  findReferencedItems,
  getHalfPortions,
  findItemReferencing,
  getExtraPortions,
  customizationsPartiallyEqual,
  isExtraPortionItemCardCustomization,
  isMealBuilderResultMessage,
  MealBuilderResultMessage,
} from '@/services/meal-builder/utils';
import { ErrorEvent } from '@/services/analytics/events';
import { MUTATIONS } from '@/store/meal-builder';

export class AppSingleEntreeTypeMealBuilderService extends AbstractMealBuilderItemVariantService<SingleEntreeTypeBuilderMode> {
  // This operation is tricky because we need to deal with migrating contents when switching primary variants
  // Scenarios:
  //  * No variant -> select variant -> primary variant
  //  * Variant w/o Extra Portion
  //    * w/ or w/o secondary variants -> select less expensive variant -> same primary variant + new secondary variant as half portion
  //    * w/ or w/o secondary variants -> select more expensive variant -> new primary variant + secondary variants as half portions
  //    * w/ secondary variants -> deselect less expensive variant -> same primary variant + secondary variants as half portions
  //    * w/ secondary variants -> deselect more expensive variant -> new primary variant + modified secondary variants as half portions
  //    * w/o secondary variants -> deselect variant -> no variant
  //  * Variant w Extra Portion
  //    * w/ or w/o secondary variants -> select less expensive variant -> new primary variant + new secondary variants as half portions
  //    * w/ or w/o secondary variants -> select more expensive variant -> same primary variant + new secondary variant as half portion
  //    * w/ secondary variants -> deselect less expensive variant -> new primary variant + new secondary variants as half portions
  //    * w/ secondary variants -> deselect more expensive variant -> same primary variant + modified secondary variants as half portions
  //    * w/o secondary variants -> deselect variant -> no variant
  selectVariantSafe(
    builderMode: SingleEntreeTypeBuilderMode,
    chosenVariant: SelectableVariantMenuItem,
    currentVariant: SelectableVariantMenuItem | null,
    allVariants: SelectableVariantMenuItem[],
    args?: any,
    shouldTrackAnalytics: boolean = true,
    isEditMode: boolean = false,
  ) {
    const title = this.getVariantsItemSectionTitle(builderMode);
    // First, figure out if we're deselecting a variant and if that variant is the current one
    const deselecting = this.isItemDisplayedAsSelected(builderMode, chosenVariant);
    const deselectingCurrentVariant = deselecting && currentVariant && chosenVariant.item.itemId == currentVariant.item.itemId;
    const callTrackAnalytics = (
      selectableMenuItem: SelectableMenuItem | null,
      selectableVariant: SelectableVariantMenuItem | null,
      actionSelected: 'add' | 'remove' | 'portion' | 'customize',
      actionValue?: string,
    ): void => {
      if (shouldTrackAnalytics) {
        this.mealBuilderAnalytics.trackItemAnalytics(title, selectableMenuItem, selectableVariant, actionSelected, actionValue);
      }
    };

    if (deselecting || deselectingCurrentVariant) {
      this.logger.debug('de-selecting variant', { chosenVariant: chosenVariant.item.itemId });
    }

    // Next: figure out what variants we're working with
    // This will be variants for all selected half-portions on the current variant (excluding the actual variant) plus the chosen variant (unless deselecting)
    const currentVariantPortions = (currentVariant && currentVariant.portions) || [];

    let selectedVariants = uniqBy(
      [
        // Add half- and extra-portion variants
        ...findReferencedItems(this.itemSelector.getSelectedItems(currentVariantPortions), allVariants),

        // Add chosen variant
        ...(!deselecting ? [chosenVariant] : []),

        // Add the current variant
        ...(!deselectingCurrentVariant && currentVariant ? [currentVariant] : []),
      ],
      v => v.item.itemId,
    );

    // If we're only ever allowed to select one variant, let the user know
    if (!deselecting && currentVariant && currentVariant.item.maxHalfs == 0) {
      chosenVariant.message = makeUniqueValue(`Can't go halfsies with ${currentVariant && currentVariant.item.primaryFillingName}`);
      this.analytics.send(new ErrorEvent('builder', chosenVariant.message.value, 'item-selection'));
      return;
    }

    if (deselecting) {
      // Variants alway require a minimum of one selection. If there is only one allowed variant (e.g. BYO Chips),
      // do not allow the customer to deselect an item and force themselves into an error state
      if (allVariants.length == 1) {
        chosenVariant.message = makeUniqueValue(`Always included`);
        this.analytics.send(new ErrorEvent('builder', chosenVariant.message.value, 'item-selection'));
        return;
      }

      selectedVariants = selectedVariants.filter(i => i.item.itemId != chosenVariant.item.itemId);
    }

    const result = this.recalibrateVariantSelection(builderMode, currentVariant, chosenVariant, selectedVariants, allVariants, true);

    if (isMealBuilderResultMessage(result)) {
      chosenVariant.message = makeUniqueValue(result.message);
      return;
    }

    callTrackAnalytics(null, chosenVariant, deselecting ? 'remove' : 'add');

    if (result && !isEditMode) {
      // Re-select default contents because they may have changed between variants
      this.selectDefaultContents(builderMode, result);
    }

    this.validateMissingSection(title);
  }

  // When selecting a variant customization, we're either dealing with a regular customization or we're
  // working with a selectable item customization
  //
  // If any of the following conditions are true, we can treat this like a regular customization selection:
  //   * There are no secondary variants
  //   * The customization type is a plain customization
  //   * The customization is of type SelectableItem but is not for ExtraPortion
  //
  // Otherwise, we have to handle what it means to have an "extra portion" of the selected variant(s)
  //
  // This is where things get weird: if we're selecting the "Extra Portion" customization and
  // there is only one variant selected, then we proceed as normal. Otherwise, we need to
  // switch to the cheapest secondary variant and apply extra portions to the more
  // expensive variants!
  //
  // If we're deselecting the "Extra Portion" customization, we need to switch back to the more expensive
  // variant.
  toggleVariantCustomizationSafe(
    builderMode: SingleEntreeTypeBuilderMode,
    variant: SelectableVariantMenuItem,
    allVariants: SelectableVariantMenuItem[],
    customization: ItemCardCustomization,
    shouldTrackAnalytics: boolean = true,
  ) {
    const title = this.getVariantsItemSectionTitle(builderMode);
    const selecting = !customization.selected;
    const selectedSecondaryVariants = findReferencedItems(
      this.itemSelector.getSelectedItems(variant.portions),
      allVariants.filter(v => v.item.itemId != variant.item.itemId),
    );
    const callTrackAnalytics = (
      primaryVariant: SelectableVariantMenuItem,
      secondaryVariant: SelectableVariantMenuItem | null,
      action: 'add' | 'remove' | 'portion' | 'customize',
      actionValue?: string,
    ) => {
      if (shouldTrackAnalytics) {
        // NOTE: For Google Analytics we use the verbiage of 'normal-portion' when the selection
        // is a 'Half Portion'. Additionally that aligns with the UI using the words 'normal' and 'double'.
        actionValue = actionValue === PortionItemType.HalfPortion ? 'normal-portion' : actionValue;
        this.mealBuilderAnalytics.trackItemAnalytics(title, secondaryVariant, primaryVariant, action, actionValue);
      }
    };

    const isNormalPortionCustomization = customization.type == ItemCardCustomizationType.Normal;
    const isExtraPortionCustomization = isExtraPortionItemCardCustomization(customization);
    const isStandardCustomization =
      selectedSecondaryVariants.length == 0 ||
      customization.type == ItemCardCustomizationType.Customization ||
      (!isExtraPortionCustomization && !isNormalPortionCustomization);

    // If this is a standard customization, bail out and handle the selection normally
    if (isStandardCustomization) {
      this.itemSelector.toggleItemCustomizationSafe(variant, customization, null, variant, false, false);
      // the the product (ie entree) section would be different when selecting protein(s).
      callTrackAnalytics(variant, null, 'portion', selecting ? PortionItemType.ExtraPortion : PortionItemType.HalfPortion);
      return;
    }

    // If this is a Normal customization selection we need to clear
    // all customizations on the selected variant, select the most expensive variant,
    // and convert extra portions for secondary variants to half-portions.
    if (isNormalPortionCustomization) {
      // Deselect all other customizations and select this one
      for (const itemCustomization of variant.customizations) {
        itemCustomization.selected = false;
      }

      // NOTE: The portions here are reversed unlike standard or extra portion customizations.
      const targetPortionType = selecting ? PortionItemType.HalfPortion : PortionItemType.ExtraPortion;
      for (const item of selectedSecondaryVariants) {
        this.convertPortionsTo(item.item.itemId, targetPortionType, variant.portions);
        callTrackAnalytics(variant, item, 'portion', targetPortionType);
      }

      this.recalibrateVariantSelection(builderMode, variant, null, [variant, ...selectedSecondaryVariants], allVariants, false);

      customization.selected = true;
      callTrackAnalytics(variant, null, 'portion', targetPortionType);
    }
    // otherwise just toggle the customization selection
    else if (isExtraPortionCustomization) {
      this.itemSelector.toggleItemCustomizationSafe(variant, customization, null, variant, false);

      const targetPortionType = selecting ? PortionItemType.ExtraPortion : PortionItemType.HalfPortion;
      for (const item of selectedSecondaryVariants) {
        this.convertPortionsTo(item.item.itemId, targetPortionType, variant.portions);
        callTrackAnalytics(variant, item, 'portion', targetPortionType);
      }

      this.recalibrateVariantSelection(builderMode, variant, null, [variant, ...selectedSecondaryVariants], allVariants, false);
      callTrackAnalytics(variant, null, 'portion', targetPortionType);
    }

    return true;
  }

  getAdditionalItemSections(builderMode: SingleEntreeTypeBuilderMode) {
    const sidesAndDrinks: MealBuilderItemSection[] = [
      {
        id: 'Side',
        title: 'Side',
        hidden: false,
        showSelectNone: false,
        items: builderMode.sides,
        maxCustomizations: null,
        minQuantity: null,
        maxQuantity: null,
      },
      {
        id: 'Drink',
        title: 'Drink',
        hidden: false,
        showSelectNone: false,
        items: builderMode.drinks,
        maxCustomizations: null,
        minQuantity: null,
        maxQuantity: null,
      },
    ];

    return sidesAndDrinks;
  }

  getVariants(builderMode: SingleEntreeTypeBuilderMode) {
    return builderMode.entree.fillings;
  }

  getVariantsItemSectionTitle(mode: BuilderMode): string {
    return getVariantsItemSectionTitle(this.store.state.menu.metadata, (mode as SingleEntreeTypeBuilderMode).entree.type);
  }

  getVariantsItemSectionSubtitle(mode: BuilderMode, variant: SelectableVariantMenuItem): string | undefined {
    const maxItems = variant.item.maxHalfs + 1;
    return maxItems > 1 ? `Choose up to ${numberToWords(maxItems)}.` : '';
  }

  getDefaultVariant(builderMode: SingleEntreeTypeBuilderMode): SelectableVariantMenuItem {
    return builderMode.entree.fillings[0];
  }

  protected migrateVariantContentsToNewVariant(
    builderMode: SingleEntreeTypeBuilderMode,
    newVariant: SelectableVariantMenuItem,
    shallowMigration: boolean = false,
  ): SelectableMenuItem[] {
    const itemSections = this.getItemSections(builderMode, newVariant);

    const migratableItems = this.itemSelector
      .getAllSelectedItems()
      .filter(i => i.itemSource == SelectableMenuItemSource.VariantContents && !isPortionItemType(i.item.itemType))
      .map(i => {
        // Try to find the corresponding item in the new variant by item id first but fall back to item name if we can't find it by id
        let newItem;
        // Grouped item customizations (like cauli rice extra) should be migrated regardless of other conditions if there is a matching grouped item customization
        if (
          i.groupedItemSelectionOptions &&
          i.defaultItemId !== i.item.itemId &&
          newVariant.contents.some(vi => vi.groupedItemSelectionOptions?.some(groupedItem => groupedItem.id === i.item.itemId))
        ) {
          // We must replace the new variant's grouped items if there is a selected grouped item that needs to migrate.
          // e.g. CMG-1036 exists as cauli rice default menu item, but if CMG-1039 (extra cauli rice) is selected and needs to migrate, we must replace the reference to the menu item
          const newVariantContentsIdx = newVariant.contents.findIndex(vi => vi.item.itemId === i.defaultItemId);
          newVariant.contents[newVariantContentsIdx] = i;
        }

        if (!newItem) {
          newItem = newVariant.contents.find(vi => vi.item.itemId === i.item.itemId);
        }

        if (!newItem) {
          this.logger.debug('Failed to find corresponding item in new variant by item id -- falling back to match on item name. This may be an error!');

          newItem = newVariant.contents.find(vi => vi.item.itemName == i.item.itemName);
        }

        return {
          old: i,
          new: newItem,
          itemSection: itemSections.find(s => !!s.items.find(si => si == i)) || null,
        };
      });

    const unmigratableItems = migratableItems.filter(i => !isDefined(i.new));
    if (unmigratableItems.length) {
      // This is currently unreachable because currently all varients are completely migratable. If in the future non-migratable varients are added this may need to be addressed.
      this.logger.error('Unable to migrate to new variant!', {
        unmigratableItems: unmigratableItems.map(i => ({ new: i.new?.item.itemId, old: i.old.item.itemId, section: i.itemSection?.title })),
      });
    }

    const toSelect = [];
    const toDeselect = [];
    for (const item of migratableItems) {
      if (!item.new) {
        this.logger.unreachable('Tried to migrate item to an undefined new item', { old: item.old.item.itemId, section: item.itemSection?.title });

        continue;
      }

      toSelect.push(item.new);

      // If the items are the same, there's nothing to migrate
      if (item.new == item.old) {
        continue;
      }

      if (item.new.selectionInfo.kind != item.old.selectionInfo.kind) {
        this.logger.unreachable('old and new items had mismatched selection info while migrating item', {
          new: { id: item.new.item.itemId, kind: item.new.selectionInfo.kind },
          old: { id: item.old.item.itemId, kind: item.old.selectionInfo.kind },
        });

        continue;
      }

      // Migrate the selection info from the old -> new item without doing a deep selection
      // Because we do a "shallow selection" we'll need to synchronize later, but it means we can perform a bulk operation
      // (which is significantly faster)
      this.itemSelector.copySelectionTo(item.new, item.old, true);

      // Check for any customization porting that needs to happen
      const oldCustomization = item.old.customizations.find(i => i.selected);
      if (oldCustomization) {
        oldCustomization.selected = false;
        const newCustomization = item.new.customizations.find(c => customizationsPartiallyEqual(oldCustomization, c));
        if (newCustomization) {
          this.itemSelector.toggleItemCustomizationSafe(item.new, newCustomization, item.itemSection, newVariant, false);
        } else {
          this.logger.unreachable(`Could not find corresponding customization for new item while migrating old customization for item`, {
            oldCustomization: oldCustomization.text,
            item: { old: item.old.item.itemId, new: item.new.item.itemId },
          });
        }
      }

      toDeselect.push(item.old);
    }

    this.itemSelector.deselectAllItems(toDeselect, shallowMigration);

    const migratedItems = toSelect.concat(toDeselect);
    if (!shallowMigration) {
      this.itemSelector.synchronizeItems(migratedItems);
    }

    // Clear out item messages that may have tried to pop during migration
    for (const item of migratedItems) {
      item.message = null;
    }

    return migratedItems;
  }

  protected completelyDeselectVariant(variant: SelectableMenuItem, shallowDeselect: boolean = false): SelectableMenuItem[] {
    const deselectedItems = this.itemSelector.getSelectedItems([variant, ...(variant.portions || [])]);
    this.itemSelector.deselectAllItems(deselectedItems, shallowDeselect);

    for (const customization of variant.customizations) {
      customization.selected = false;
    }

    return deselectedItems;
  }

  protected convertPortionsTo(itemId: string, targetPortion: PortionItemType, portions: SelectableMenuItem[]) {
    const halfPortions = getHalfPortions(portions);
    const extraPortions = getExtraPortions(portions);

    const halfPortion = findItemReferencing(halfPortions, itemId);
    if (halfPortion) {
      if (targetPortion == PortionItemType.HalfPortion) {
        this.itemSelector.selectItem(halfPortion);
      } else {
        this.itemSelector.deselectItem(halfPortion);
      }
    }

    const extraPortion = findItemReferencing(extraPortions, itemId);
    if (extraPortion) {
      if (targetPortion == PortionItemType.ExtraPortion) {
        this.itemSelector.selectItem(extraPortion);
      } else {
        this.itemSelector.deselectItem(extraPortion);
      }
    }
  }

  protected partitionVariantsByPrice(
    variant: SelectableVariantMenuItem,
    selectedSecondaryVariants: SelectableVariantMenuItem[],
    pickMostExpensive: boolean,
  ): [SelectableVariantMenuItem, SelectableVariantMenuItem[]] {
    const { getItemPrice } = this.menu;
    let newVariant = [variant, ...selectedSecondaryVariants].sort(sortFn(i => getItemPrice(i.item), pickMostExpensive))[0];
    if (getItemPrice(newVariant.item) == getItemPrice(variant.item)) {
      newVariant = variant;
    }

    const newSecondaryVariants = [variant, ...selectedSecondaryVariants].filter(i => i.item.itemId != newVariant.item.itemId);

    return [newVariant, newSecondaryVariants];
  }

  protected recalibrateVariantSelection(
    builderMode: SingleEntreeTypeBuilderMode,
    currentVariant: SelectableVariantMenuItem | null,
    selectedVariant: SelectableVariantMenuItem | null,
    selectedVariants: SelectableVariantMenuItem[],
    allVariants: SelectableVariantMenuItem[],
    migrateVariantCustomizations: boolean,
  ): SelectableVariantMenuItem | null | MealBuilderResultMessage {
    const desynchronizedItems = [] as SelectableMenuItem[];

    // Check if we're deselecting the last variant (so we can bail early)
    if (!selectedVariants.length) {
      this.logger.debug('Deselecting all variants');

      const defaultVariant = this.getDefaultVariant(builderMode);
      if (!defaultVariant) {
        throw new Error('No default variant found!');
      }

      this.setSelectedVariant(null);
      desynchronizedItems.push(...this.migrateVariantContentsToNewVariant(builderMode, defaultVariant, true));

      if (currentVariant) {
        desynchronizedItems.push(...this.completelyDeselectVariant(currentVariant, true));
      }
      this.itemSelector.synchronizeItems(desynchronizedItems);

      return null;
    }

    // Next: figure out what the new primary variant should be
    //   * If an extra portion has been selected it should be the least expensive variant
    const hasExtraPortion = currentVariant?.portions.find(p => this.itemSelector.isItemSelected(p) && p.item.itemType == PortionItemType.ExtraPortion);

    const newVariant = selectedVariants.sort(sortFn(i => this.menu.getItemPrice(i.item), !hasExtraPortion))[0];
    if (!newVariant) {
      this.logger.unreachable('Failed to find new variant from possible variants', { possibleVariants: getItemIds(selectedVariants) });

      throw new Error('Failed to find new variant from possible variants');
    }

    // Now that we have the new variant, the rest of the variants will become portions
    const secondaryVariants = selectedVariants.filter(v => v.item.itemId != newVariant.item.itemId);

    if (currentVariant && newVariant.item.itemId == currentVariant.item.itemId) {
      if (secondaryVariants.length) {
        this.logger.debug('Selecting new portions for existing primary variant', {
          newPortions: getItemIds(secondaryVariants),
          existingPrimaryVariant: newVariant.item.itemId,
        });
      } else {
        this.logger.debug('Clearing portions on existing primary variant', { variant: newVariant.item.itemId });
      }
    } else if (secondaryVariants.length) {
      this.logger.debug('Selecting new primary variant and migrating existing variants to portions', {
        newVariant: newVariant.item.itemId,
        portions: getItemIds(secondaryVariants),
      });
    } else {
      this.logger.debug('Selecting variant', { newVariant: newVariant.item.itemId });
    }

    const secondaryVariantPortionSource = hasExtraPortion ? getExtraPortions(newVariant.portions) : getHalfPortions(newVariant.portions);
    const portionsForSecondaryVariants = [];
    for (const variant of secondaryVariants) {
      const portion = findItemReferencing(secondaryVariantPortionSource, variant.item.itemId);
      if (!portion) {
        this.logger.warn('Failed to find portion item for item in list', { item: variant.item.itemId, portions: getItemIds(secondaryVariantPortionSource) });

        const message = `Can't go halfsies with ${selectedVariant && selectedVariant.item.primaryFillingName}`;
        this.analytics.send(new ErrorEvent('builder', message, 'item-selection'));

        return {
          type: 'error',
          message
        };
      }

      portionsForSecondaryVariants.push(portion);
    }

    // Next: make sure we're allowed to select another variant:

    // The max is the total number of extras + halves we're allowed + 1 (for the primary variant)
    const maxSelectableVariants = newVariant.item.maxHalfs + 1;
    if (selectedVariants.length > maxSelectableVariants) {
      this.logger.warn(`Attempted to add more half-portions (${selectedVariants.length}) than allowed by variant's max (${maxSelectableVariants})`);
      
      const message = `You can only select ${maxSelectableVariants} filling${maxSelectableVariants == 1 ? '' : 's'}`;
      this.analytics.send(new ErrorEvent('builder', message, 'item-selection'));
      
      return {
        type: 'error',
        message,
      };
    }

    // Only set the variant if
    //   * there is no variant currently selected
    //   * the most new variant is a different variant than the currently selected one

    // Keep track of customizations we may have to restore at the end of process
    const restorableCustomizations = [] as ItemCardCustomization[];

    const changingVariants = !currentVariant || currentVariant.item.itemId != newVariant.item.itemId;
    if (changingVariants) {
      this.setSelectedVariant(newVariant);

      const selectedItems = this.itemSelector.getAllSelectedItems();
      this.logger.debug('Migrating selectedItem to new variant', { selectedItems: selectedItems.map(i => i.item.itemId), newVariant: newVariant.item.itemId });

      desynchronizedItems.push(...this.migrateVariantContentsToNewVariant(builderMode, newVariant, true));

      // Try to migrate portion customizations if possible
      if (currentVariant) {
        const portionCustomizations = currentVariant.customizations
          .filter(c => c.selected && c.type == ItemCardCustomizationType.SelectableItem && !!c.portion)
          .map(c => newVariant.customizations.find(nc => nc.type == ItemCardCustomizationType.SelectableItem && nc.portion == (c as SelectableItemCardCustomization).portion))
          .filter(isDefined);

        restorableCustomizations.push(...portionCustomizations);
      }
    } else if (currentVariant) {
      restorableCustomizations.push(...currentVariant.customizations.filter(c => c.selected));
    }

    if (currentVariant) {
      desynchronizedItems.push(...this.completelyDeselectVariant(currentVariant, true));
    }

    // Finally:
    //  * select the variant
    //  * select secondary variant portion
    //  * restore any customizations we flagged earlier

    this.itemSelector.selectItemShallow(newVariant);
    desynchronizedItems.push(newVariant);

    for (const item of portionsForSecondaryVariants) {
      this.itemSelector.selectItemShallow(item);
      desynchronizedItems.push(item);
    }

    this.itemSelector.synchronizeItems(desynchronizedItems);

    if (migrateVariantCustomizations) {
      for (const customization of restorableCustomizations) {
        this.toggleVariantCustomizationSafe(builderMode, newVariant, allVariants, customization);
      }
    }

    if (hasExtraPortion) {
      const extraPortionForNewVariant = newVariant.customizations.find(c => isExtraPortionItemCardCustomization(c));
      if (!extraPortionForNewVariant) {
        this.logger.unreachable('Could not find extra portion customization for new variant', { newVariant: newVariant.item.itemId });
        throw new Error('Could not find extra portion customization');
      }
      
      if (selectedVariant?.item.itemId !== newVariant?.item.itemId && currentVariant?.item.itemId !== newVariant?.item.itemId && !secondaryVariants.length) {
        this.itemSelector.toggleItemCustomizationSafe(newVariant, extraPortionForNewVariant, null, newVariant, false);
      } else {
        extraPortionForNewVariant.selected = true;
      }

      for (const secondaryVariant of secondaryVariants) {
        const extraPortionForSecondaryVariant = secondaryVariant.customizations.find(c => isExtraPortionItemCardCustomization(c));
        if (!extraPortionForSecondaryVariant) {
          this.logger.unreachable('Could not find extra portion customization for secondary variant', { secondaryVariant: secondaryVariant.item.itemId });
          throw new Error('Could not find extra portion customization');
        }

        extraPortionForSecondaryVariant.selected = true;
      }

      if (selectedVariant?.item.itemId !== currentVariant?.item.itemId && !secondaryVariants.length) {
        const deselectedSecondaryVariant = allVariants.find(v => v.item.itemId == selectedVariant?.item.itemId);
        const extraPortionForDeselectedSecondaryVariant = deselectedSecondaryVariant?.customizations.find(c => isExtraPortionItemCardCustomization(c));

        if (extraPortionForDeselectedSecondaryVariant) {
          extraPortionForDeselectedSecondaryVariant.selected = false;
        } 
      }
    } else {
      const extraPortionForNewVariant = newVariant.customizations.find(c => isExtraPortionItemCardCustomization(c));
      if (extraPortionForNewVariant) {
        extraPortionForNewVariant.selected = false;
      }
    }

    return newVariant;
  }

  getVariantPriceType(builderMode: SingleEntreeTypeBuilderMode): PriceType {
    return builderMode.entree.type == 'BYOChips' ? PriceType.Single : PriceType.Default;
  }

  private validateMissingSection(sectionTitle: string) {
    const currentMissingSections = this.store.state.mealBuilder.missingItemSections;

    if (currentMissingSections.indexOf(sectionTitle) > -1) {
      this.store.commit(MUTATIONS.SET_MISSING_ITEM_SECTIONS, currentMissingSections.filter(x => x != sectionTitle));
    }
  }
}
